import { useMemo, useState } from "react";
import { Link } from "wouter";
import { ArrowRight, Calendar, ChevronLeft, ChevronRight, MapPin, Plus, Search, Users, X } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import {
  useGroupsList,
  useGroupsCrud,
  useStandardsList,
  useUsersByRole,
  useAllSessions,
} from "@/store/hooks";
import { useDemoStore } from "@/store/useDemoStore";
import type { Group, GroupSession, GroupSessionTipo } from "@/data";

const TIPO_COLORS: Record<GroupSessionTipo, string> = {
  induccion: "#0e7490",
  teoria: "#1a2744",
  practica: "#0891b2",
  examen: "#d97706",
  cierre: "#16a34a",
};

const TIPO_LABEL: Record<GroupSessionTipo, string> = {
  induccion: "Inducción",
  teoria: "Teoría",
  practica: "Práctica",
  examen: "Examen",
  cierre: "Cierre",
};

export default function GroupsCalendar() {
  const groups = useGroupsList();
  const standards = useStandardsList();
  const instructors = useUsersByRole("instructor");
  const evaluators = useUsersByRole("evaluador");
  const allSessions = useAllSessions();
  const groupsCrud = useGroupsCrud();
  const logEvent = useDemoStore((s) => s.logEvent);
  const setSessionsForGroup = useDemoStore((s) => s.setSessionsForGroup);

  const [search, setSearch] = useState("");
  const [standardFilter, setStandardFilter] = useState<string>("");
  const [sedeFilter, setSedeFilter] = useState<string>("");
  const [estadoFilter, setEstadoFilter] = useState<string>("");
  const [showWizard, setShowWizard] = useState(false);

  const sedes = useMemo(() => Array.from(new Set(groups.map((g) => g.sede))).sort(), [groups]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (q && !g.nombre.toLowerCase().includes(q) && !g.generacion.toLowerCase().includes(q)) return false;
      if (standardFilter && g.estandar !== standardFilter) return false;
      if (sedeFilter && g.sede !== sedeFilter) return false;
      if (estadoFilter && g.estado !== estadoFilter) return false;
      return true;
    });
  }, [groups, search, standardFilter, sedeFilter, estadoFilter]);

  return (
    <AppLayout breadcrumb={["Grupos y generaciones"]}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>Grupos y generaciones</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>
            {groups.length} generaciones · {filtered.length} visibles
          </div>
        </div>
        <PrimaryButton testId="button-new-group" onClick={() => setShowWizard(true)}>
          <Plus size={13} /> Nueva generación
        </PrimaryButton>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 10 }}>
          <div style={{ position: "relative" }}>
            <Search size={14} color="var(--cc-gray-400)" style={{ position: "absolute", left: 10, top: 10 }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o generación…"
              data-testid="input-search-groups"
              style={inputStyle({ paddingLeft: 30 })}
            />
          </div>
          <select value={standardFilter} onChange={(e) => setStandardFilter(e.target.value)} data-testid="filter-standard" style={inputStyle()}>
            <option value="">Todos los estándares</option>
            {standards.map((s) => <option key={s.codigo} value={s.codigo}>{s.codigo}</option>)}
          </select>
          <select value={sedeFilter} onChange={(e) => setSedeFilter(e.target.value)} data-testid="filter-sede" style={inputStyle()}>
            <option value="">Todas las sedes</option>
            {sedes.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={estadoFilter} onChange={(e) => setEstadoFilter(e.target.value)} data-testid="filter-estado" style={inputStyle()}>
            <option value="">Todos los estados</option>
            <option value="planeado">Planeado</option>
            <option value="en-curso">En curso</option>
            <option value="finalizado">Finalizado</option>
          </select>
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 16 }}>
        {filtered.length === 0 && (
          <Card>
            <div style={{ fontSize: 13, color: "var(--cc-gray-500)", textAlign: "center", padding: 18 }}>
              Sin generaciones que coincidan con los filtros.
            </div>
          </Card>
        )}
        {filtered.map((g) => {
          const instr = instructors.find((i) => i.id === g.instructorId);
          const evalIds = g.evaluadoresIds ?? [g.evaluadorId];
          return (
            <Card key={g.id} testId={`card-group-${g.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{g.estandar} · Generación {g.generacion}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "var(--cc-navy)", marginTop: 2 }}>{g.nombre}</div>
                </div>
                <Badge tone={g.estado === "en-curso" ? "green" : g.estado === "planeado" ? "amber" : "gray"}>
                  {g.estado === "en-curso" ? "En curso" : g.estado === "planeado" ? "Planeado" : "Finalizado"}
                </Badge>
              </div>

              <Row icon={Calendar} text={`${g.inicio} → ${g.fin}`} />
              <Row icon={MapPin} text={g.sede} />
              <Row icon={Users} text={`${g.candidatosIds.length} / ${g.cupo} inscritos · ${Math.max(0, g.cupo - g.candidatosIds.length)} cupos libres`} />

              <div style={{ marginTop: 14, padding: 12, background: "var(--cc-gray-50)", borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600, marginBottom: 4 }}>Equipo asignado</div>
                <div style={{ fontSize: 12, color: "var(--cc-gray-700)", lineHeight: 1.5 }}>
                  Instructor: <strong>{instr?.nombre ?? "—"}</strong><br />
                  Evaluadores: <strong>{evalIds.length}</strong> · {evalIds.map((id) => evaluators.find((e) => e.id === id)?.nombre ?? "—").join(", ")}
                </div>
              </div>

              <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>
                  {(allSessions.filter((s) => s.grupoId === g.id)).length} sesiones programadas
                </span>
                <Link href={`/admin/grupos/${g.id}`} data-testid={`link-open-group-${g.id}`}>
                  <PrimaryButton size="sm">Abrir <ArrowRight size={12} /></PrimaryButton>
                </Link>
              </div>
            </Card>
          );
        })}
      </div>

      <div style={{ marginTop: 16 }}>
        <MonthlyCalendar sessions={allSessions} groups={groups} />
      </div>

      {showWizard && (
        <NewGroupWizard
          onClose={() => setShowWizard(false)}
          onCreate={(g, sessions) => {
            groupsCrud.add(g);
            if (sessions.length > 0) setSessionsForGroup(g.id, sessions);
            logEvent({ actor: "Administrador", rol: "admin", accion: "creó generación", entidad: "group", entidadId: g.id });
            setShowWizard(false);
          }}
          standards={standards.map((s) => s.codigo)}
          instructors={instructors.map((i) => ({ id: i.id, nombre: i.nombre }))}
          evaluators={evaluators.map((e) => ({ id: e.id, nombre: e.nombre, capacidad: e.capacidad ?? 12, asignados: countAssigned(e.id, groups) }))}
        />
      )}
    </AppLayout>
  );
}

function countAssigned(evalId: string, groups: Group[]) {
  return groups.reduce((acc, g) => acc + ((g.evaluadoresIds ?? [g.evaluadorId]).includes(evalId) ? g.candidatosIds.length : 0), 0);
}

function MonthlyCalendar({ sessions, groups }: { sessions: GroupSession[]; groups: Group[] }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(2026, 4, 1));
  const [pickerDate, setPickerDate] = useState<string | null>(null);
  const activeGroups = groups.filter((g) => g.estado !== "finalizado");
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const daysIn = new Date(year, month + 1, 0).getDate();
  const cells: Array<{ d: number | null; iso: string | null }> = [];
  for (let i = 0; i < startDow; i++) cells.push({ d: null, iso: null });
  for (let d = 1; d <= daysIn; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ d, iso });
  }
  const sessByDay = new Map<string, GroupSession[]>();
  for (const s of sessions) {
    const arr = sessByDay.get(s.fecha) ?? [];
    arr.push(s);
    sessByDay.set(s.fecha, arr);
  }

  const monthName = first.toLocaleDateString("es-MX", { month: "long", year: "numeric" });
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <SectionTitle>Calendario · {monthName}</SectionTitle>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev" style={navBtn}><ChevronLeft size={14} /></button>
          <button onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} data-testid="cal-today" style={{ ...navBtn, width: "auto", padding: "4px 10px", fontSize: 12 }}>Hoy</button>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next" style={navBtn}><ChevronRight size={14} /></button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((d) => (
          <div key={d} style={{ fontSize: 10, fontWeight: 700, color: "var(--cc-gray-500)", textTransform: "uppercase", padding: 4, textAlign: "center" }}>{d}</div>
        ))}
        {cells.map((c, i) => {
          if (!c.d) return <div key={i} />;
          const list = (c.iso && sessByDay.get(c.iso)) || [];
          const isToday = c.iso === todayIso;
          return (
            <div key={i}
              onClick={() => c.iso && setPickerDate(c.iso)}
              data-testid={`cal-day-${c.iso}`}
              style={{
                minHeight: 78,
                border: isToday ? "2px solid var(--cc-teal)" : "1px solid var(--cc-gray-200)",
                borderRadius: 6,
                padding: 4,
                background: isToday ? "var(--cc-teal-bg)" : "white",
                cursor: "pointer",
              }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--cc-gray-500)", marginBottom: 2, display: "flex", justifyContent: "space-between" }}>
                <span>{c.d}</span>
                {list.length === 0 && <Plus size={9} color="var(--cc-gray-400)" />}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {list.slice(0, 3).map((s) => {
                  const g = groups.find((gg) => gg.id === s.grupoId);
                  return (
                    <Link key={s.id} href={`/admin/grupos/${s.grupoId}?tab=sesiones&s=${s.id}`} data-testid={`cal-evt-${s.id}`} onClick={(e) => e.stopPropagation()} style={{ textDecoration: "none" }}>
                      <div style={{
                        fontSize: 10,
                        background: TIPO_COLORS[s.tipo],
                        color: "white",
                        padding: "2px 4px",
                        borderRadius: 3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                      }} title={`${s.titulo} · ${g?.nombre ?? ""}`}>
                        {s.hora} {s.titulo}
                      </div>
                    </Link>
                  );
                })}
                {list.length > 3 && <div style={{ fontSize: 10, color: "var(--cc-gray-500)" }}>+{list.length - 3} más</div>}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
        {(Object.keys(TIPO_COLORS) as GroupSessionTipo[]).map((t) => (
          <div key={t} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--cc-gray-600)" }}>
            <div style={{ width: 10, height: 10, background: TIPO_COLORS[t], borderRadius: 2 }} />
            {TIPO_LABEL[t]}
          </div>
        ))}
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--cc-gray-500)" }}>Clic en un día para crear una sesión.</div>
      </div>

      {pickerDate && (
        <div onClick={() => setPickerDate(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: 12, padding: 22, width: "min(420px, 100%)" }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 4 }}>Nueva sesión · {pickerDate}</h2>
            <div style={{ fontSize: 12, color: "var(--cc-gray-500)", marginBottom: 12 }}>Elija el grupo para programar la sesión.</div>
            {activeGroups.length === 0 && <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>No hay grupos activos.</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflow: "auto" }}>
              {activeGroups.map((g) => (
                <Link key={g.id} href={`/admin/grupos/${g.id}?tab=sesiones&date=${pickerDate}`} data-testid={`cal-pick-${g.id}`}
                  onClick={() => setPickerDate(null)}
                  style={{ display: "block", padding: "10px 12px", border: "1px solid var(--cc-gray-200)", borderRadius: 8, textDecoration: "none", color: "var(--cc-navy)", fontSize: 13, fontWeight: 600 }}>
                  {g.nombre}
                  <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 400 }}>{g.estandar} · {g.sede}</div>
                </Link>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <PrimaryButton variant="outline" onClick={() => setPickerDate(null)}>Cancelar</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

const navBtn: React.CSSProperties = {
  width: 28, height: 28, border: "1px solid var(--cc-gray-200)", background: "white",
  borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
};

function inputStyle(extra: React.CSSProperties = {}): React.CSSProperties {
  return { width: "100%", padding: "8px 10px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, fontSize: 13, background: "white", ...extra };
}

function Row({ icon: Icon, text }: { icon: React.ComponentType<{ size?: number; color?: string }>; text: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 12, color: "var(--cc-gray-700)" }}>
      <Icon size={14} color="var(--cc-gray-400)" /> {text}
    </div>
  );
}

interface WizardEvaluator { id: string; nombre: string; capacidad: number; asignados: number }
interface WizardInstructor { id: string; nombre: string }

function NewGroupWizard({ onClose, onCreate, standards, instructors, evaluators }: {
  onClose: () => void;
  onCreate: (g: Group, sessions: GroupSession[]) => void;
  standards: string[];
  instructors: WizardInstructor[];
  evaluators: WizardEvaluator[];
}) {
  const [estandar, setEstandar] = useState(standards[0] ?? "EC1781");
  const [generacion, setGeneracion] = useState("2026-C");
  const [sede, setSede] = useState("Centro de Adiestramiento Iztapalapa, CDMX");
  const [inicio, setInicio] = useState("2026-07-01");
  const [fin, setFin] = useState("2026-08-15");
  const [cupo, setCupo] = useState(10);
  const [instructorId, setInstructorId] = useState(instructors[0]?.id ?? "");
  const [evalIds, setEvalIds] = useState<string[]>(evaluators[0] ? [evaluators[0].id] : []);
  const [error, setError] = useState<string | null>(null);

  function toggleEval(id: string) {
    const ev = evaluators.find((e) => e.id === id);
    if (!ev) return;
    if (evalIds.includes(id)) {
      setEvalIds(evalIds.filter((x) => x !== id));
    } else {
      if (ev.asignados + cupo > ev.capacidad) {
        setError(`${ev.nombre} excede su capacidad (${ev.asignados} ya asignados + ${cupo} nuevos > ${ev.capacidad})`);
        return;
      }
      setError(null);
      setEvalIds([...evalIds, id]);
    }
  }

  function handleCreate() {
    if (!estandar || !generacion || !sede || !inicio || !fin || !instructorId || evalIds.length === 0) {
      setError("Complete todos los campos y asigne al menos un evaluador.");
      return;
    }
    if (new Date(fin) < new Date(inicio)) {
      setError("La fecha de fin debe ser posterior al inicio.");
      return;
    }
    const overloaded = evalIds
      .map((id) => evaluators.find((e) => e.id === id)!)
      .filter((e) => e.asignados + cupo > e.capacidad);
    if (overloaded.length > 0) {
      setError(`Capacidad excedida para: ${overloaded.map((e) => `${e.nombre} (${e.asignados}+${cupo}>${e.capacidad})`).join("; ")}. Reduzca el cupo o quite evaluadores.`);
      return;
    }
    const id = `grp-${Date.now().toString(36)}`;
    const group: Group = {
      id,
      nombre: `${estandar} — Generación ${generacion}`,
      estandar,
      generacion,
      inicio,
      fin,
      sede,
      instructorId,
      evaluadorId: evalIds[0],
      evaluadoresIds: evalIds,
      cupo,
      candidatosIds: [],
      estado: "planeado",
      documentos: [],
    };
    const sessions: GroupSession[] = [
      { id: `ses-${id}-1`, grupoId: id, titulo: "Inducción", tipo: "induccion", fecha: inicio, hora: "09:00", duracionMin: 180, sede, instructorId },
      { id: `ses-${id}-2`, grupoId: id, titulo: "Cierre y dictamen", tipo: "cierre", fecha: fin, hora: "10:00", duracionMin: 180, sede, instructorId, evaluadorId: evalIds[0] },
    ];
    onCreate(group, sessions);
  }

  return (
    <div onClick={onClose} style={modalOverlay}>
      <div onClick={(e) => e.stopPropagation()} style={modalBody}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: "var(--cc-navy)" }}>Nueva generación</h2>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer" }} data-testid="wizard-close"><X size={18} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Estándar">
            <select value={estandar} onChange={(e) => setEstandar(e.target.value)} data-testid="wiz-standard" style={inputStyle()}>
              {standards.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Generación">
            <input value={generacion} onChange={(e) => setGeneracion(e.target.value)} data-testid="wiz-gen" style={inputStyle()} />
          </Field>
          <Field label="Sede">
            <input value={sede} onChange={(e) => setSede(e.target.value)} data-testid="wiz-sede" style={inputStyle()} />
          </Field>
          <Field label="Cupo">
            <input type="number" min={1} value={cupo} onChange={(e) => setCupo(Number(e.target.value) || 1)} data-testid="wiz-cupo" style={inputStyle()} />
          </Field>
          <Field label="Inicio">
            <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} data-testid="wiz-inicio" style={inputStyle()} />
          </Field>
          <Field label="Fin">
            <input type="date" value={fin} onChange={(e) => setFin(e.target.value)} data-testid="wiz-fin" style={inputStyle()} />
          </Field>
          <Field label="Instructor">
            <select value={instructorId} onChange={(e) => setInstructorId(e.target.value)} data-testid="wiz-instructor" style={inputStyle()}>
              {instructors.map((i) => <option key={i.id} value={i.id}>{i.nombre}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: "var(--cc-gray-500)", fontWeight: 600, marginBottom: 6 }}>Evaluadores asignados (capacidad por persona)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {evaluators.map((e) => {
              const checked = evalIds.includes(e.id);
              const wouldExceed = !checked && e.asignados + cupo > e.capacidad;
              return (
                <label key={e.id} style={{
                  display: "flex", alignItems: "center", gap: 8, padding: 8,
                  border: `1px solid ${checked ? "var(--cc-teal)" : "var(--cc-gray-200)"}`,
                  borderRadius: 6, cursor: wouldExceed ? "not-allowed" : "pointer",
                  background: checked ? "var(--cc-teal-bg)" : "white",
                  opacity: wouldExceed ? 0.55 : 1,
                }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleEval(e.id)} data-testid={`wiz-eval-${e.id}`} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>{e.nombre}</div>
                    <div style={{ fontSize: 10, color: "var(--cc-gray-500)" }}>
                      {e.asignados} / {e.capacidad} candidatos asignados {wouldExceed && "· excede capacidad"}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        {error && (
          <div style={{ marginTop: 10, padding: 8, background: "var(--cc-red-bg)", color: "var(--cc-red)", borderRadius: 6, fontSize: 12 }} data-testid="wiz-error">
            {error}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <PrimaryButton variant="outline" onClick={onClose}>Cancelar</PrimaryButton>
          <PrimaryButton onClick={handleCreate} testId="wiz-create">Crear generación</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}

const modalOverlay: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex",
  alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20,
};
const modalBody: React.CSSProperties = {
  background: "white", borderRadius: 12, padding: 22, width: "min(640px, 100%)",
  maxHeight: "90vh", overflow: "auto",
};
